import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Plus,
  Search,
  TrendingUp,
  Eye,
  Trash2,
  LayoutGrid,
  List,
  Target,
  Phone,
  Mail,
  Calendar,
  ImageIcon,
} from "lucide-react";
import api from "../lib/api";

const STATUSES = [
  { value: "to_contact", label: "Da Contattare", color: "bg-slate-500" },
  { value: "contacted", label: "Contattato", color: "bg-blue-500" },
  { value: "presentation_sent", label: "Presentazione Inviata", color: "bg-purple-500" },
  { value: "catalog_sent", label: "Catalogo Inviato", color: "bg-indigo-500" },
  { value: "negotiation", label: "In Trattativa", color: "bg-orange-500" },
  { value: "won", label: "Vinto", color: "bg-green-500" },
  { value: "lost", label: "Perso", color: "bg-gray-500" },
];

const SOURCES = ["Web", "Phone", "Email", "Referral", "Trade Show", "Fiera", "Other"];
const CHANNELS = [
  { value: "Retail", label: "Retail / Normal Trade" },
  { value: "GDO", label: "GDO" },
  { value: "DO", label: "DO" },
  { value: "Ho.Re.Ca.", label: "Ho.Re.Ca." },
  { value: "Distributore", label: "Distributore" },
];

const emptyLead = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  channel: "Retail",
  source: "Web",
  status: "to_contact",
  value: 0,
  probability: 10,
  notes: "",
  next_action_date: "",
};

export default function LeadsPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyLead);
  const [selectedAgentId, setSelectedAgentId] = useState("");

  useEffect(() => {
    fetchLeads();
    if (isAdmin) {
      fetchAgents();
    }
  }, [isAdmin]);

  const fetchLeads = async () => {
    try {
      const response = await api.get("/leads");
      setLeads(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei lead");
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await api.get("/users?role=agent");
      setAgents(response.data);
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (selectedAgentId) {
        payload.agent_id = selectedAgentId;
      }
      
      await api.post("/leads", payload);
      toast.success("Lead creato con successo");
      setDialogOpen(false);
      setFormData(emptyLead);
      setSelectedAgentId("");
      fetchLeads();
    } catch (error) {
      toast.error("Errore nella creazione del lead");
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm("Eliminare questo lead?")) return;
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Lead eliminato");
      fetchLeads();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      const lead = leads.find((l) => l.id === leadId);
      await api.put(`/leads/${leadId}`, { ...lead, status: newStatus });
      toast.success("Status aggiornato");
      fetchLeads();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const getAgentName = (agentId) => {
    if (!agentId) return "N/A";
    const agent = agents.find((a) => a.id === agentId);
    return agent?.name || "N/A";
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value || 0);
  };

  const getLeadsByStatus = (status) => {
    return filteredLeads.filter((lead) => lead.status === status);
  };

  // Apply filters
  let filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      lead.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || lead.status === statusFilter;
    const matchesSource = !sourceFilter || lead.source === sourceFilter;
    return matchesSearch && matchesStatus && matchesSource;
  });

  // Stats
  const stats = {
    total: leads.length,
    won: leads.filter((l) => l.status === "won").length,
    pipeline: leads.filter((l) => !["won", "lost"].includes(l.status)).length,
    totalValue: leads.reduce((sum, l) => sum + (l.value || 0), 0),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="w-6 h-6" />
            Lead Management
          </h1>
          <p className="text-muted-foreground">
            Gestisci la tua pipeline di vendita
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="icon"
              onClick={() => setViewMode("list")}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "kanban" ? "default" : "ghost"}
              size="icon"
              onClick={() => setViewMode("kanban")}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Lead
            </Button>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Nuovo Lead</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Azienda *</Label>
                    <Input
                      required
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Referente *</Label>
                    <Input
                      required
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefono</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Canale</Label>
                    <Select
                      value={formData.channel}
                      onValueChange={(v) => setFormData({ ...formData, channel: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CHANNELS.map((ch) => (
                          <SelectItem key={ch.value} value={ch.value}>{ch.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Fonte</Label>
                    <Select
                      value={formData.source}
                      onValueChange={(v) => setFormData({ ...formData, source: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SOURCES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {isAdmin && (
                    <div className="space-y-2">
                      <Label>Assegna a Agente</Label>
                      <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessuno</SelectItem>
                          {agents.map((agent) => (
                            <SelectItem key={agent.id} value={agent.id}>
                              {agent.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Note</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit">Crea Lead</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.total}</div>
            <p className="text-muted-foreground text-sm">Totale Lead</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-green-600">{stats.won}</div>
            <p className="text-muted-foreground text-sm">Vinti</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-blue-600">{stats.pipeline}</div>
            <p className="text-muted-foreground text-sm">In Pipeline</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{formatCurrency(stats.totalValue)}</div>
            <p className="text-muted-foreground text-sm">Valore Pesato</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Cerca per azienda, referente, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Tutti gli status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli status</SelectItem>
            {STATUSES.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${status.color}`} />
                  {status.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={(v) => setSourceFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Tutte le fonti" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le fonti</SelectItem>
            {SOURCES.map((source) => (
              <SelectItem key={source} value={source}>{source}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {viewMode === "list" ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Azienda</th>
                    <th className="text-left p-4 font-medium">Referente</th>
                    {isAdmin && <th className="text-left p-4 font-medium">Agente</th>}
                    <th className="text-left p-4 font-medium">Canale</th>
                    <th className="text-left p-4 font-medium">Fonte</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-right p-4 font-medium">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.length === 0 ? (
                    <tr>
                      <td colSpan={isAdmin ? "7" : "6"} className="text-center p-8 text-muted-foreground">
                        Nessun lead trovato
                      </td>
                    </tr>
                  ) : (
                    filteredLeads.map((lead) => {
                      const statusInfo = STATUSES.find(s => s.value === lead.status) || STATUSES[0];
                      return (
                        <tr key={lead.id} className="border-b hover:bg-muted/50">
                          <td className="p-4">
                            <div 
                              className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                              onClick={() => navigate(`/leads/${lead.id}`)}
                            >
                              {lead.company_name}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {lead.email}
                            </div>
                          </td>
                          <td className="p-4">
                            <div>{lead.contact_name}</div>
                            {lead.phone && (
                              <div className="text-sm text-muted-foreground flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {lead.phone}
                              </div>
                            )}
                          </td>
                          {isAdmin && (
                            <td className="p-4">
                              <div className="text-sm">{getAgentName(lead.agent_id)}</div>
                            </td>
                          )}
                          <td className="p-4">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                              {lead.channel}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="text-sm">{lead.source_detail || lead.source}</div>
                            {lead.business_card && (
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/fiere/business-cards/${lead.business_card}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-purple-600 hover:underline mt-1"
                              >
                                <ImageIcon className="w-3 h-3" />
                                Biglietto
                              </a>
                            )}
                          </td>
                          <td className="p-4">
                            <Select
                              value={lead.status}
                              onValueChange={(value) => handleStatusChange(lead.id, value)}
                            >
                              <SelectTrigger className="w-40">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUSES.map((status) => (
                                  <SelectItem key={status.value} value={status.value}>
                                    <div className="flex items-center gap-2">
                                      <div className={`w-2 h-2 rounded-full ${status.color}`} />
                                      {status.label}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate(`/leads/${lead.id}`)}
                                title="Visualizza dettaglio"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(lead.id)}
                                className="text-destructive hover:bg-destructive/10"
                                title="Elimina"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        // Kanban View
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {STATUSES.filter((s) => s.value !== "won" && s.value !== "lost").map((status) => (
            <div key={status.value} className="space-y-3">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${status.color}`} />
                <h3 className="font-semibold">{status.label}</h3>
                <span className="ml-auto text-sm text-muted-foreground">
                  {getLeadsByStatus(status.value).length}
                </span>
              </div>
              <div className="space-y-2">
                {getLeadsByStatus(status.value).map((lead) => (
                  <Card
                    key={lead.id}
                    className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                  >
                    <div className="space-y-2">
                      <div className="font-medium text-sm">{lead.company_name}</div>
                      <div className="text-xs text-muted-foreground">{lead.contact_name}</div>
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                          {lead.channel}
                        </span>
                        {lead.source_detail && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                            {lead.source_detail.length > 12 ? lead.source_detail.substring(0, 12) + '...' : lead.source_detail}
                          </span>
                        )}
                      </div>
                      {lead.next_action_date && (
                        <div className="flex items-center gap-1 text-xs text-orange-600">
                          <Calendar className="w-3 h-3" />
                          {new Date(lead.next_action_date).toLocaleDateString("it-IT")}
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
